import React, { useState } from "react";
import { TbPencil, TbTrash, TbPlayerPlay, TbCopy, TbPlus } from "react-icons/tb";
import { useLeadMagnets } from "../../hooks/useLeadMagnets";
import { fmtDate, fmtDateTime, fmtRelative } from "../../lib/dates";
import { isWidgetUsed, embedOf, lastUsedOf } from "../../lib/widgetUsage";
import { Button, EmptyState, SkeletonRows, ConfirmModal, Alert } from "../../components";
import "./Scanner.css";

// The widgets that exist, as a screen of its own.
//
// This was a tab inside the builder, which put the list of what has been built behind the
// thing that builds more of them: to see your widgets you first opened a blank wizard. The
// two are different jobs — one creates, one looks after what exists — and only the second
// is where someone goes to find a key, check whether a widget is earning anything, or
// retire one.
//
// Editing navigates to the builder with the widget's key in the path, so a widget has an
// address and "edit this one" survives a refresh or a shared link.

const SavedWidgetsScreen = ({ onNavigate }) => {
  const { widgets, loading, error, remove } = useLeadMagnets();
  const [confirm, setConfirm] = useState(null);
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [copied, setCopied] = useState(null);

  // Unused: no visitor has ever run a scan with it. A widget scanned with but never
  // given a lead is working — it is just not converting yet — so it is not "unused".
  const unusedCount = widgets.filter((w) => !isWidgetUsed(w)).length;
  const shown = unusedOnly ? widgets.filter((w) => !isWidgetUsed(w)) : widgets;

  // The hosted widget page: the same URL the iframe embed points at, and the same bundle
  // a visitor loads. Opening it is therefore a real end-to-end test — the live config, a
  // real scan, the real report — rather than a preview that renders a second time from
  // different code and can disagree with what customers see.
  // ?test=1 keeps these runs out of the widget's scan count and embed tracking.
  const testUrl = (key) => `${window.location.origin}/s2/public/widget/${key}?test=1`;

  const copyKey = async (key) => {
    try { await navigator.clipboard.writeText(key); setCopied(key); setTimeout(() => setCopied(null), 1800); }
    catch { /* clipboard blocked; the key is on screen to copy by hand */ }
  };

  if (loading) return <div className="v2-page"><SkeletonRows rows={5} cols={8} /></div>;

  if (!widgets.length) {
    return (
      <div className="v2-page">
        <Alert>{error}</Alert>
        <EmptyState title="No widgets yet">
          Build one from Lead magnets and it will appear here.
          <div style={{ marginTop: ".75rem" }}>
            <Button variant="primary" onClick={() => onNavigate?.("lead-magnet")}>
              <TbPlus size={15} /> Build a lead magnet
            </Button>
          </div>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="v2-page">
      <Alert>{error}</Alert>

      <div className="v2-builder-bar" style={{ margin: "0 0 .5rem" }}>
        <span className="v2-section-title">Saved widgets</span>
        <span className="v2-hint" style={{ marginLeft: ".5rem" }}>
          {unusedCount > 0
            ? `${unusedCount} of ${widgets.length} have never been used for a scan.`
            : "Every widget has been used for at least one scan."}
        </span>
        {unusedCount > 0 && (
          <Button style={{ marginLeft: "auto" }} onClick={() => setUnusedOnly((v) => !v)}>
            {unusedOnly ? "Show all" : "Show unused only"}
          </Button>
        )}
        <Button variant="primary" style={{ marginLeft: unusedCount > 0 ? 0 : "auto" }}
                onClick={() => onNavigate?.("lead-magnet")}>
          <TbPlus size={15} /> New widget
        </Button>
      </div>

      <div className="v2-tablewrap">
        <table className="v2-table">
          <thead>
            <tr>
              <th>Title</th><th>Key</th><th>Embedded on</th><th>Last used</th>
              <th className="v2-num">Leads</th><th>Last lead</th><th>Created</th>
              <th className="v2-num">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((w) => {
              const leads = Number(w.leadCount) || 0;
              const embed = embedOf(w);
              const lastUsed = lastUsedOf(w);
              return (
                <tr key={w.widgetKey}>
                  <td>
                    {w.title || w.buttonText || "Untitled widget"}
                    {/* The point of the column: a widget built and never used looks
                        identical to a working one until you can see this. */}
                    {!isWidgetUsed(w) && <span className="v2-pill v2-pill--idle">Unused</span>}
                  </td>
                  <td className="v2-mono v2-dim">
                    <button type="button" className="v2-keycopy" title="Copy widget key"
                            onClick={() => copyKey(w.widgetKey)}>
                      {w.widgetKey} <TbCopy size={13} />
                    </button>
                    {copied === w.widgetKey && <span className="v2-hint"> copied</span>}
                  </td>
                  <td>
                    {embed ? (
                      <span className="v2-embedsite" title={embed.lastSeenAt ? `Last loaded ${fmtDateTime(embed.lastSeenAt)}` : undefined}>
                        <span className="v2-embedsite-dot" aria-hidden="true" />
                        <span className="v2-embedsite-host">{embed.host}</span>
                        {embed.lastSeenAt && <span className="v2-dim"> · seen {fmtRelative(embed.lastSeenAt)}</span>}
                      </span>
                    ) : (
                      <span className="v2-dim" title="No site outside Pipsqueak has loaded this widget yet">Not detected</span>
                    )}
                  </td>
                  <td className="v2-dim">
                    {lastUsed ? (lastUsed.dateOnly ? fmtDate(lastUsed.at) : fmtDateTime(lastUsed.at)) : "—"}
                  </td>
                  <td className="v2-num">{leads}</td>
                  <td className="v2-dim">{w.lastLeadAt ? fmtDateTime(w.lastLeadAt) : "—"}</td>
                  <td className="v2-dim">{w.createdAt ? fmtDateTime(w.createdAt) : "—"}</td>
                  <td className="v2-num">
                    {/* Opens the hosted page rather than embedding anything, so a widget
                        can be run end to end before it goes near a customer's site. */}
                    <Button icon title="Test this widget"
                            onClick={() => window.open(testUrl(w.widgetKey), "_blank", "noopener")}>
                      <TbPlayerPlay size={15} />
                    </Button>
                    {" "}
                    <Button icon title="Edit" onClick={() => onNavigate?.("lead-magnet", w.widgetKey)}>
                      <TbPencil size={15} />
                    </Button>
                    {" "}
                    <Button icon variant="danger" title="Delete" onClick={() => setConfirm(w)}>
                      <TbTrash size={15} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirm && (
        <ConfirmModal title="Delete this widget?"
          body="The embed stops working immediately on any site using it. Leads already captured are kept."
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm.widgetKey); setConfirm(null); }} />
      )}
    </div>
  );
};

export default SavedWidgetsScreen;
